import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

/**
 * Three-dot row actions menu — the single source of truth for per-row action lists across the
 * app. Matches the Assets table pattern: kebab button opens a dropdown of labelled actions,
 * each with an icon. Keeps tables tidy when row actions grow beyond 2-3 buttons, and gives
 * every page the same affordance.
 *
 * Props:
 *   items: Array of {
 *     key:       unique string (for React)
 *     label:     visible text
 *     icon:      optional lucide icon component
 *     iconClass: tailwind class for the icon color (default 'text-gray-400')
 *     onClick:   handler — menu auto-closes after
 *     danger:    boolean — if true, renders in red (destructive actions)
 *     hidden:    boolean — pass true to skip rendering this item (state-dependent actions)
 *     disabled:  boolean — disables the item but still shows it (so the user knows it exists)
 *     subtitle:  optional small text under the label (e.g. "Same-day only")
 *   }
 *
 * Falsy items are filtered out, so callers can use ternaries inline without sprinkling
 * `.filter(Boolean)` everywhere.
 *
 * Keyboard: Enter, Space or ArrowDown on the button opens the menu with focus on the first item
 * (ArrowUp, on the last). The arrow keys, Home and End move between items, a letter jumps to the
 * next item starting with it, and Enter or Space runs one. Escape closes the menu and returns focus
 * to the button; Tab closes it and moves on from the button. The menu used to open and then be
 * unreachable: its items came last in the page, so Tab went elsewhere and closed it, and actions
 * such as a patient's Edit and Delete lived nowhere else.
 */
const MENU_WIDTH = 192; // tailwind w-48
const MENU_MARGIN = 4;  // mt-1

const ITEM_SELECTOR = '[role="menuitem"]:not(:disabled)';

const RowActionsMenu = ({ items = [], buttonClassName = '', align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // { top, left, openUp }
  // Which item takes focus once the menu has rendered: 'first', 'last', or null after a mouse click,
  // which leaves focus where it was.
  const [focusOnOpen, setFocusOnOpen] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Position the menu relative to the button's viewport rect. Rendered via portal at body
  // level so parent `overflow-hidden` containers (rounded table cards, scrollable modals)
  // can't clip the dropdown. Auto-flips to open upward when the row is near the bottom of
  // the viewport — the standard "menu falls into view, not out of it" pattern.
  const computePosition = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const estimatedMenuHeight = Math.min(items.filter(i => i && !i.hidden).length, 8) * 44 + 12;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedMenuHeight + 16 && rect.top > estimatedMenuHeight;
    const top = openUp
      ? rect.top - MENU_MARGIN
      : rect.bottom + MENU_MARGIN;
    const left = align === 'left'
      ? rect.left
      : rect.right - MENU_WIDTH;
    // Clamp inside the viewport so the menu never overflows horizontally.
    const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8));
    setMenuPos({ top, left: clampedLeft, openUp });
  };

  // Close on outside click. Mousedown (not click) so the menu closes before any pending
  // button-click on a different row fires.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = (e) => {
      // Scrolling inside the menu itself is no reason to close it.
      if (e && e.type === 'scroll' && menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    // Escape closes the menu from wherever focus is, and closes only the menu: it is taken in the
    // capture phase and stopped, so a dialog the menu sits in stays open.
    const onDocKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onDocKeyDown, true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onDocKeyDown, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !menuPos || !focusOnOpen || !menuRef.current) return;
    const list = menuRef.current.querySelectorAll(ITEM_SELECTOR);
    const target = focusOnOpen === 'last' ? list[list.length - 1] : list[0];
    target?.focus();
  }, [open, menuPos, focusOnOpen]);

  const visible = items.filter(Boolean).filter(i => !i.hidden);
  if (visible.length === 0) return null;

  const menuItems = () => Array.from(menuRef.current?.querySelectorAll(ITEM_SELECTOR) || []);

  const openMenu = (focus) => {
    computePosition();
    setFocusOnOpen(focus);
    setOpen(true);
  };

  const toggle = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    // A click made with Enter or Space has no pointer behind it (detail is 0). Only then does focus
    // move into the menu; a mouse click leaves it alone.
    openMenu(e.detail === 0 ? 'first' : null);
  };

  const onButtonKeyDown = (e) => {
    // Rows and cards often act on Enter or Space themselves. Kept from them, so the key opens this
    // menu instead of being taken over by the row around it.
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    e.stopPropagation();
    if (!open) {
      openMenu(e.key === 'ArrowUp' ? 'last' : 'first');
      return;
    }
    const list = menuItems();
    (e.key === 'ArrowUp' ? list[list.length - 1] : list[0])?.focus();
  };

  const onMenuKeyDown = (e) => {
    if (e.key === 'Tab') {
      // Not prevented: focus goes back to the button first, so the browser's Tab carries on from the
      // button rather than from the end of the page, where the menu is rendered.
      setOpen(false);
      btnRef.current?.focus();
      return;
    }
    // Kept from the row around the menu (see onButtonKeyDown), whatever the key does here.
    e.stopPropagation();
    const list = menuItems();
    if (list.length === 0) return;
    const at = list.indexOf(document.activeElement);
    const moveTo = (i) => {
      e.preventDefault();
      list[(i + list.length) % list.length].focus();
    };
    if (e.key === 'ArrowDown') moveTo(at + 1);
    else if (e.key === 'ArrowUp') moveTo(at < 0 ? list.length - 1 : at - 1);
    else if (e.key === 'Home') moveTo(0);
    else if (e.key === 'End') moveTo(list.length - 1);
    else if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const letter = e.key.toLowerCase();
      for (let step = 1; step <= list.length; step += 1) {
        const i = (at + step) % list.length;
        if ((list[i].textContent || '').trim().toLowerCase().startsWith(letter)) {
          moveTo(i);
          break;
        }
      }
    }
  };

  const choose = (item) => {
    if (item.disabled) return;
    setOpen(false);
    // Focus is parked on the button before the action runs, so a dialog the action opens hands focus
    // back to this row when it closes, instead of to nothing (the item is gone by then).
    btnRef.current?.focus();
    item.onClick?.();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${buttonClassName}`}
        title="More actions"
      >
        <MoreHorizontal className="h-5 w-5 text-gray-500" />
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: MENU_WIDTH,
            transform: menuPos.openUp ? 'translateY(-100%)' : 'none',
          }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-[100]"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={onMenuKeyDown}
        >
          {visible.map((item) => {
            const Icon = item.icon;
            const colorCls = item.danger
              ? 'text-red-600 hover:bg-red-50 focus:bg-red-50'
              : 'text-gray-700 hover:bg-gray-50 focus:bg-gray-100';
            const iconColor = item.danger ? 'text-red-500' : (item.iconClass || 'text-gray-400');
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                onClick={() => choose(item)}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm focus:outline-none ${colorCls} ${
                  item.disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {Icon && <Icon className={`h-4 w-4 ${iconColor}`} />}
                <span className="flex-1 text-left">
                  <span className="block">{item.label}</span>
                  {item.subtitle && (
                    <span className="block text-[10px] text-gray-400 font-normal">{item.subtitle}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};

export default RowActionsMenu;
